import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DataBand from "../components/DataBand";
import PaywallModal from "../components/PaywallModal";
import { ArticlesAPI, normalizeArticle } from "../lib/api";
import { Link } from "react-router-dom";
import SmartImage from "../components/SmartImage";
import { Loader2, Lock, ArrowRight } from "lucide-react";

/**
 * Institutional Homepage — Vexent Research
 *
 * Not a news site. Renders as a research firm's public catalog:
 *   1. Manifesto band — who Vexent is, in three lines
 *   2. Featured Report — the most recent report as a full-width hero
 *   3. Report Archive — every other Professional report as a compact
 *      catalog row (title, one-line thesis, date, access CTA)
 *   4. Subscription CTA — $2,000/year Professional or Access Code
 *
 * No ticker (replaced by a minimal DataBand at the top of the page),
 * no left/right news rails, no chronological river.
 */
const HomePage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ArticlesAPI.list({ limit: 100 })
      .then((data) => {
        if (cancelled) return;
        const sorted = data
          .map(normalizeArticle)
          .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
        setReports(sorted);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const featured = reports[0];
  const archive = reports.slice(1);

  return (
    <div className="bg-white min-h-screen text-[#0B1F33]">
      <DataBand />
      <Header />

      {/* Manifesto */}
      <section className="border-b border-[#D7DEE5] bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-6 py-14 md:py-20">
          <div className="uppercase tracking-[0.28em] text-[11px] text-[#0891B2] font-condensed font-semibold mb-4" data-testid="manifesto-eyebrow">
            Vexent Research · Institutional Intelligence
          </div>
          <h1 className="font-serif-display text-[34px] md:text-[52px] leading-[1.05] font-bold text-[#0B1F33] max-w-4xl">
            Decision-grade research for institutions that need to see the next decade, not the next headline.
          </h1>
          <p className="mt-6 md:mt-8 max-w-3xl text-[16px] md:text-[18px] leading-[1.65] text-neutral-700">
            Vexent publishes one or two long-form strategic reports each week,
            integrating economics, geopolitics, technology, energy, regulation
            and demographics into single-question deep dives. Access is restricted
            to professional subscribers.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setPaywallOpen(true)}
              data-testid="hero-subscribe-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0B1F33] hover:bg-[#132f57] text-white uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors"
            >
              Subscribe · $2,000 / year
            </button>
            <button
              onClick={() => setPaywallOpen(true)}
              data-testid="hero-access-code-btn"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#0B1F33] hover:bg-[#0B1F33] hover:text-white text-[#0B1F33] uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors"
            >
              <Lock className="w-3 h-3" /> Redeem Access Code
            </button>
          </div>
        </div>
      </section>

      {/* Reports catalog */}
      <section className="max-w-[1200px] mx-auto px-6 py-14 md:py-20">
        <div className="flex items-baseline justify-between border-b border-[#0B1F33] pb-3 mb-10">
          <h2 className="font-serif-display text-[24px] md:text-[28px] font-bold" data-testid="reports-heading">
            Current Research
          </h2>
          <span className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-condensed font-semibold">
            {reports.length} report{reports.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24 text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading catalog…
          </div>
        )}

        {!loading && featured && (
          <FeaturedReport report={featured} />
        )}

        {!loading && archive.length > 0 && (
          <div className="mt-14">
            <h3 className="font-serif-display text-[20px] font-bold mb-6 uppercase tracking-[0.15em] text-neutral-700">
              Archive
            </h3>
            <div className="divide-y divide-[#D7DEE5] border-y border-[#D7DEE5]">
              {archive.map((r) => (
                <ArchiveRow key={r.id} report={r} />
              ))}
            </div>
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className="py-24 text-center text-neutral-500">
            <p className="text-[15px]">The first Vexent report is in preparation. Check back shortly.</p>
          </div>
        )}
      </section>

      <SubscribeCta onOpen={() => setPaywallOpen(true)} />

      <Footer />
      {paywallOpen && (
        <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
      )}
    </div>
  );
};

const FeaturedReport = ({ report }) => {
  const dek = (report.dek || "").trim();
  return (
    <Link
      to={`/article/${report.slug}`}
      data-testid={`featured-report-${report.slug}`}
      className="group grid md:grid-cols-[1.15fr_1fr] gap-6 md:gap-10 items-start"
    >
      {report.image && (
        <div className="overflow-hidden bg-neutral-100 aspect-[16/10]">
          <SmartImage
            src={report.image}
            alt={report.title}
            eager
            sizes="(min-width: 768px) 55vw, 100vw"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
          />
        </div>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#7a1c1c] font-condensed font-semibold">
          Featured Report · {formatDate(report.publishedAt)}
        </div>
        <h3 className="mt-4 font-serif-display text-[28px] md:text-[38px] leading-[1.1] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
          {report.title}
        </h3>
        {dek && (
          <p className="mt-5 text-[15px] md:text-[16px] leading-[1.65] text-neutral-700 line-clamp-6">
            {dek}
          </p>
        )}
        <div className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-[#0B1F33] group-hover:text-[#0891B2]">
          Read the report <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
};

const ArchiveRow = ({ report }) => (
  <Link
    to={`/article/${report.slug}`}
    data-testid={`archive-row-${report.slug}`}
    className="group grid grid-cols-[80px_1fr_auto] md:grid-cols-[110px_1fr_auto] gap-4 md:gap-6 items-start py-6"
  >
    {report.image ? (
      <div className="overflow-hidden bg-neutral-100 aspect-[4/3]">
        <SmartImage src={report.image} alt={report.title} sizes="110px" className="w-full h-full object-cover" />
      </div>
    ) : <div />}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-condensed font-semibold">
        {formatDate(report.publishedAt)} · {report.category || "Research"}
      </div>
      <div className="mt-1 font-serif-display text-[19px] md:text-[22px] leading-[1.2] font-bold text-[#0B1F33] group-hover:text-[#0891B2] transition-colors line-clamp-2">
        {report.title}
      </div>
      {report.dek && (
        <p className="hidden md:block mt-2 text-[13.5px] leading-[1.55] text-neutral-600 line-clamp-2">
          {report.dek}
        </p>
      )}
    </div>
    <ArrowRight className="w-4 h-4 mt-2 text-neutral-400 group-hover:text-[#0891B2] transition-colors shrink-0" />
  </Link>
);

const SubscribeCta = ({ onOpen }) => (
  <section className="bg-[#0B1F33] text-white">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-24 grid md:grid-cols-[1.3fr_1fr] gap-10 items-start">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#B8E9F1] font-condensed font-semibold">
          Professional Access
        </div>
        <h3 className="mt-4 font-serif-display text-[28px] md:text-[36px] leading-[1.1] font-bold">
          One tier. Institutional grade. $2,000 per year.
        </h3>
        <ul className="mt-6 space-y-3 text-[15px] text-white/80 leading-[1.6]">
          <li>· Full text and PDF of every Vexent report</li>
          <li>· Audio narration of every report</li>
          <li>· Selective early access to reports in preparation</li>
          <li>· Analyst notes and update briefs between reports</li>
        </ul>
      </div>
      <div className="bg-white/5 border border-white/15 p-6 md:p-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#B8E9F1] font-condensed font-semibold">
          Two ways in
        </div>
        <button
          onClick={onOpen}
          data-testid="cta-subscribe-btn"
          className="mt-4 w-full px-6 py-4 bg-[#B8E9F1] hover:bg-white text-[#0B1F33] uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors"
        >
          Subscribe — $2,000 / year
        </button>
        <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-white/40 font-condensed">
          <div className="flex-1 h-px bg-white/15" /> or <div className="flex-1 h-px bg-white/15" />
        </div>
        <button
          onClick={onOpen}
          data-testid="cta-access-code-btn"
          className="w-full px-6 py-4 border border-white/40 hover:bg-white/10 text-white uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-2"
        >
          <Lock className="w-3.5 h-3.5" /> Redeem access code
        </button>
        <p className="mt-4 text-[11px] text-white/50 leading-relaxed">
          Access codes are issued to invited institutional clients and rotated monthly.
        </p>
      </div>
    </div>
  </section>
);

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }).toUpperCase();
  } catch {
    return "";
  }
};

export default HomePage;
